/*
 * Copyright (C) 2025 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Location } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { AssignmentReviewContextDetails } from '@app/app/page-modules/course/models/course-assignments.model';
import { PortfolioUpdateRequestModel } from '@app/app/page-modules/portfolio/models';
import { Page } from '@app/app/shared/models/page';
import { PortfolioModel } from '@app/app/shared/models/portfolio/portfolio.model';
import { PortfolioDataService } from '@app/app/shared/services/portfolio/data.service';
import { TranslocoService } from '@ngneat/transloco';
import { Observable, catchError, map, switchMap } from 'rxjs';
import { RestClientService } from 'src/app/shared/services/rest-client.service';
import { ObservableResult } from 'src/app/shared/store';
import { environment } from 'src/environments/environment';

// Portfolios live on the resource microservice; respect the local override (environment.dev-custom.ts) if set.
const RESOURCE_API_ROOT = environment.localServiceOverrides?.resourcesBaseUrl || environment.apiRootUrl || '';

export class ApiPortfolioDataService implements PortfolioDataService {
  constructor(
    private client: RestClientService,
    private translocoService: TranslocoService,
  ) {}

  getPortfolio(portfolioId: string): ObservableResult<PortfolioModel> {
    return this.client.get<PortfolioModel>(Location.joinWithSlash(RESOURCE_API_ROOT, `portfolios/${portfolioId}`), null).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetPortfolio'))),
    );
  }

  getAssignmentReview(portfolioUid: string): Observable<AssignmentReviewContextDetails[]> {
    return this.client
      .get<
        AssignmentReviewContextDetails[]
      >(Location.joinWithSlash(RESOURCE_API_ROOT, `portfolios/${portfolioUid}/assignment-review`), null)
      .pipe(map(({ body }) => body ?? []));
  }

  getUserPortfolios(page: number, pageSize: number): ObservableResult<Page<PortfolioModel>> {
    return this.client
      .get<Page<PortfolioModel>>(Location.joinWithSlash(RESOURCE_API_ROOT, `portfolios/search`), {
        page: page.toString(),
        pageSize: pageSize.toString(),
      })
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorSearchPortfolios'))),
      );
  }

  getPublicPortfolio(portfolioId: string): ObservableResult<PortfolioModel> {
    return this.client.get<PortfolioModel>(Location.joinWithSlash(RESOURCE_API_ROOT, `portfolios/public/${portfolioId}`), null).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetPortfolio'))),
    );
  }

  updatePortfolio(portfolioId: string, request: PortfolioUpdateRequestModel): ObservableResult<PortfolioModel> {
    return this.client.put<PortfolioModel>(Location.joinWithSlash(RESOURCE_API_ROOT, `portfolios/${portfolioId}`), request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateFolioStatus'))),
    );
  }

  createPortfolio(request: PortfolioUpdateRequestModel): ObservableResult<PortfolioModel> {
    return this.client.post<PortfolioModel>(Location.joinWithSlash(RESOURCE_API_ROOT, `portfolios/create`), request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateFolioStatus'))),
    );
  }

  updatePortfolioVisibility(portfolioId: string, isVisible: boolean): ObservableResult<PortfolioModel> {
    const params = new HttpParams().set('isVisible', isVisible);

    return this.client.put<PortfolioModel>(Location.joinWithSlash(RESOURCE_API_ROOT, `portfolios/${portfolioId}/visibility`), params).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateFolioStatus'))),
    );
  }

  deletePortfolio(portfolioId: string): ObservableResult<void> {
    return this.client.delete<void>(Location.joinWithSlash(RESOURCE_API_ROOT, `portfolios/${portfolioId}`)).pipe(
      switchMap(() => ObservableResult.ofSuccess(null)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateFolioStatus'))),
    );
  }

  updatePortfolioCohortShare(portfolioId: string, shared: boolean): ObservableResult<PortfolioModel> {
    return this.client
      .put<PortfolioModel>(Location.joinWithSlash(RESOURCE_API_ROOT, `portfolios/${portfolioId}/cohort-share`), { shared })
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateFolioStatus'))),
      );
  }
}
